import { supabaseAuth } from "../supabaseClient";
import { API_BASE_URL } from "./api";



interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}
interface ChatHistory {
  userId: string;
  messages: ChatMessage[];
  lastUpdated: number;
}


export class AIService {

  private loadChatHistory(userId: string): ChatHistory | null {
    try {
      const raw = localStorage.getItem(`chat_history_${userId}`);
      if (!raw) return null;
      const history = JSON.parse(raw) as ChatHistory;
      return history;
    } catch (e) {
      console.error("Load chat history error:", e);
      return null;
    }
  }

  private saveChatHistory(userId: string, messages: ChatMessage[]) {
    try {
      const history: ChatHistory = {
        userId,
        messages,
        lastUpdated: Date.now(),
      };
      localStorage.setItem(`chat_history_${userId}`, JSON.stringify(history));
    } catch (e) {
      console.error("Save chat history error:", e);
    }
  }

  getChatHistory(userId: string): ChatMessage[] {
    const history = this.loadChatHistory(userId);
    return history?.messages || [];
  }

  private appendMessage(
    userId: string,
    role: "user" | "assistant",
    content: string
  ) {
    const current = this.getChatHistory(userId);

    // Ensure unique ID by adding a small delay if needed
    let uniqueId = `${Date.now()}_${Math.random()}_${performance.now()}`;
    let timestamp = Date.now();

    // Check if last message has same timestamp, add microsecond delay
    if (
      current.length > 0 &&
      current[current.length - 1].timestamp === timestamp
    ) {
      timestamp += 1;
      uniqueId = `${timestamp}_${Math.random()}_${performance.now()}`;
    }

    const msg: ChatMessage = {
      id: uniqueId,
      role,
      content,
      timestamp,
    };
    const updated = [...current, msg];
    this.saveChatHistory(userId, updated);
  }

  private async authHeaders(): Promise<HeadersInit> {
    const {
      data: { session },
    } = await supabaseAuth.auth.getSession();
    const token = session?.access_token;
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }


  async handleUserResearchQuery(userId: string, query: string): Promise<string> {
    console.log("🔍 RESEARCH MODE ACTIVE: Calling handleUserResearchQuery with query:", query);

    try {
      // Add user message to chat history
      this.appendMessage(userId, "user", query);

      // Call the research endpoint
      const headers = await this.authHeaders();
      const response = await fetch(`${API_BASE_URL}/ai/research/intelligent`, {
          method: "POST",
          headers,
        body: JSON.stringify({ message: query, userId }),
      });

      if (!response.ok) {
        throw new Error(`Research query failed: ${response.statusText}`);
      }

      const data = await response.json();
      const reply = data.response;

      // Save assistant response to chat history
      this.appendMessage(userId, "assistant", reply);
      return reply;
    } catch (error) {
      console.error("Error in handleUserResearchQuery:", error);
      const errorReply = `❌ Research query failed: ${error instanceof Error ? error.message : "Unknown error"}`;
      this.appendMessage(userId, "assistant", errorReply);
      return errorReply;
    }
  }



}

export const aiService = new AIService();
